import { brandingService } from '@simwood/nv-react-components';
import Bowser from 'bowser';
import React, { PropsWithChildren, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NoWebRTC from '../../components/NoWebRTC';
import { isElectron, isWeb } from '../../helpers/platform';
import { testAuthAndFetchUser } from '../../reducers/user';
import { StoreDispatch, StoreState } from '../../store';
import { fetchBranding } from '../../store/thunks';
import ExternalLogin from '../ExternalLogin';
import Login from '../Login';
import * as styles from './styles.module.scss';

const PC =
  window.RTCPeerConnection ||
  window.mozRTCPeerConnection ||
  window.webkitRTCPeerConnection;

const getUserMedia =
  navigator.getUserMedia ||
  navigator.webkitGetUserMedia ||
  navigator.msGetUserMedia ||
  navigator.mozGetUserMedia;

let browserSupportsWebRTC = !!PC && !!getUserMedia;

const browser = Bowser.getParser(window.navigator.userAgent);
if (browser.getBrowserName() === 'Chrome') {
  const version = browser.getBrowserVersion();
  if (version && Number(version.split('.')[0]) <= 48) {
    browserSupportsWebRTC = false;
  }
}

const RootGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch<StoreDispatch>();
  const darkMode = useSelector((state: StoreState) => state.client.darkMode);
  const authenticated = useSelector(
    (state: StoreState) => state.user.isAuthenticated,
  );
  const accessToken = useSelector(
    (state: StoreState) => state.user.accessToken,
  );
  const isAuthTested = useSelector(
    (state: StoreState) => state.user.isAuthTested,
  );
  const isBrandingInitialized = useSelector(
    (state: StoreState) => state.branding.isInitialized,
  );
  const isLoggingInExternally = useSelector(
    (state: StoreState) => state.user.isLoggingInExternally,
  );

  useEffect(() => {
    if (isWeb()) {
      dispatch(fetchBranding());
    } else {
      brandingService.resetBranding(false);
    }
  }, [dispatch]);

  const brandingInitialized = isBrandingInitialized || isElectron();

  useEffect(() => {
    if (brandingInitialized) {
      dispatch(testAuthAndFetchUser());
    }
  }, [brandingInitialized, accessToken, dispatch]);

  let content: ReactNode;

  if (!browserSupportsWebRTC) {
    content = <NoWebRTC />;
  } else if (authenticated) {
    content = children;
  } else if (isLoggingInExternally) {
    content = <ExternalLogin />;
  } else {
    content = <Login />;
  }

  if (!isAuthTested) {
    // TODO_V2 handle branding errors

    // TODO spinner or something?
    return null;
  }

  return (
    <div className={darkMode ? styles.dark_theme : styles.base_theme}>
      {content}
    </div>
  );
};

export default RootGuard;
